import { useState } from "react";
import { isAxiosError } from "axios";
import { Dialog, DialogActions, DialogContent, Stack, useTheme } from "@mui/material";
import { Button, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import {
    usePublishActionPlanTemplate,
    useTemplatePublicationContext,
} from "src/api/client-api/actionPlanTemplates/useTemplatePublication";
import { useSnackbar } from "src/utils/useSnackbar";
import { templatePublicationFailure } from "src/api/client-api/actionPlanTemplates/templateErrors";
import { useTemplatePublicationBlocker } from "./useTemplatePublicationBlocker";

type Props = { templateId: string; onClose: () => void };

export const PublishTemplateDialog = (props: Props) => {
    const { templateId, onClose } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const snackbar = useSnackbar();
    const context = useTemplatePublicationContext(templateId);
    const mutation = usePublishActionPlanTemplate(templateId);
    const [changed, setChanged] = useState(false);
    const [sectorDetached, setSectorDetached] = useState(false);
    const [rejection, setRejection] = useState<
        "no-action" | "invalid-for-catalogue" | "unavailable-ssq-version" | null
    >(null);
    const missing = useTemplatePublicationBlocker(context.data?.template);
    const existing = context.data?.existing;
    const unavailableSsqVersion =
        context.data?.ssqVersionAvailable === false ||
        (context.data?.ssqVersionStatus !== undefined && context.data.ssqVersionStatus !== 1);
    const busy = mutation.isLoading || context.isFetching;
    const close = () => {
        if (!mutation.isLoading) onClose();
    };
    const confirm = () => {
        if (
            busy ||
            !context.data ||
            context.isError ||
            missing ||
            sectorDetached ||
            rejection !== null ||
            unavailableSsqVersion ||
            context.data.template.status !== "draft"
        )
            return;
        setChanged(false);
        setRejection(null);
        mutation.mutate(existing?.id ?? null, {
            onSuccess: (template) => {
                snackbar.setSnackbarProps({
                    type: "success",
                    message:
                        existing ?
                            t(
                                "settings.action_plan_templates.publish.replaced",
                                '"{{name}}" has been published and "{{previousName}}" archived.',
                                { name: template.name, previousName: existing.name },
                            )
                        :   t("settings.action_plan_templates.publish.success", '"{{name}}" has been published.', {
                                name: template.name,
                            }),
                });
                onClose();
            },
            onError: (error) => {
                const failure = templatePublicationFailure(error);

                if (failure.sectorDetached) {
                    setSectorDetached(true);
                    return;
                }

                if (failure.noAction || failure.invalidForCatalogue || failure.unavailableSsqVersion) {
                    setRejection(
                        failure.noAction ? "no-action"
                        : failure.invalidForCatalogue ? "invalid-for-catalogue"
                        : "unavailable-ssq-version",
                    );
                    void context.refetch();
                    return;
                }

                if (isAxiosError(error) && (error.response?.status === 409 || error.response?.status === 422)) {
                    setChanged(true);
                    void context.refetch();
                }
            },
        });
    };
    return (
        <Dialog
            open
            onClose={close}
            fullWidth
            maxWidth="sm"
            aria-labelledby={`publish-template-${templateId}`}
            onClick={(event) => event.stopPropagation()}
        >
            <Stack sx={{ px: theme.spacing(4), pt: theme.spacing(4) }}>
                <Typography id={`publish-template-${templateId}`} variant="h3">
                    {existing ?
                        t("settings.action_plan_templates.publish.replace_title", "Replace the existing template?")
                    :   t("settings.action_plan_templates.publish.title", "Publish this template?")}
                </Typography>
            </Stack>
            <DialogContent
                sx={{ px: theme.spacing(4), pt: theme.spacing(2), pb: theme.spacing(4) }}
                aria-busy={context.isFetching}
            >
                <Typography component="p" variant="body-regular">
                    {existing ?
                        t(
                            "settings.action_plan_templates.publish.replace_body",
                            '"{{name}}" is already published for this SSQ and industry sector. Publishing this one will archive it. Users who already started an action plan keep their current plan.',
                            { name: existing.name },
                        )
                    :   t(
                            "settings.action_plan_templates.publish.body",
                            "Once published, this template becomes available to users who validate a matching diagnostic.",
                        )
                    }
                </Typography>
                {context.isFetching && (
                    <Typography component="p" variant="body-regular" role="status">
                        {t("settings.action_plan_templates.publish.checking", "Checking publication conditions…")}
                    </Typography>
                )}
                {changed && (
                    <Typography component="p" variant="body-regular" role="alert">
                        {t(
                            "settings.action_plan_templates.publish.changed",
                            "The publication conditions have changed. Review them before confirming again.",
                        )}
                    </Typography>
                )}
                {sectorDetached && (
                    <Typography component="p" variant="body-regular" role="alert">
                        {t(
                            "settings.action_plan_templates.editor.sector_not_attached",
                            "This industry sector is no longer attached to the template's SSQ. Select another one before saving.",
                        )}
                    </Typography>
                )}
                {rejection === "no-action" && (
                    <Typography component="p" variant="body-regular" role="alert">
                        {t(
                            "settings.action_plan_templates.publish_requires_action",
                            "Add at least one action before publishing.",
                        )}
                    </Typography>
                )}
                {rejection === "invalid-for-catalogue" && (
                    <Typography component="p" variant="body-regular" role="alert">
                        {t(
                            "settings.action_plan_templates.publish.invalid_for_catalogue",
                            "Some actions are no longer valid for the current catalog. Edit the template and review its actions before publishing.",
                        )}
                    </Typography>
                )}
                {(unavailableSsqVersion || rejection === "unavailable-ssq-version") && (
                    <Typography component="p" variant="body-regular" role="alert">
                        {t(
                            "settings.action_plan_templates.publish.ssq_version_unavailable",
                            "The selected SSQ version is no longer available. Create a new template with another version.",
                        )}
                    </Typography>
                )}
                {missing && (
                    <Typography component="p" variant="body-regular" role="alert">
                        {missing}
                    </Typography>
                )}
                {context.data && context.data.template.status !== "draft" && (
                    <Typography component="p" variant="body-regular" role="alert">
                        {t(
                            "settings.action_plan_templates.publish.draft_only",
                            "Only draft templates can be published.",
                        )}
                    </Typography>
                )}
                {(context.isError || (mutation.isError && !changed && !sectorDetached && rejection === null)) && (
                    <Typography component="p" variant="body-regular" role="alert">
                        {t(
                            "settings.action_plan_templates.publish.failed",
                            "Unable to publish the template. Please try again.",
                        )}
                    </Typography>
                )}
                {context.isError && (
                    <Button variant="text" disabled={context.isFetching} onClick={() => void context.refetch()}>
                        {t("settings.action_plan_templates.publish.retry", "Try again")}
                    </Button>
                )}
            </DialogContent>
            <DialogActions
                sx={{
                    p: theme.spacing(4),
                    borderTop: 1,
                    borderColor: "divider",
                    flexDirection: "column",
                    gap: theme.spacing(2),
                    "& > :not(style) ~ :not(style)": { marginLeft: 0 },
                }}
            >
                <Button
                    fullWidth
                    variant="primary"
                    disabled={
                        busy ||
                        context.isError ||
                        !context.data ||
                        Boolean(missing) ||
                        sectorDetached ||
                        rejection !== null ||
                        unavailableSsqVersion ||
                        context.data.template.status !== "draft"
                    }
                    onClick={confirm}
                >
                    {existing ?
                        t("settings.action_plan_templates.publish.yes_replace", "Yes, publish and replace")
                    :   t("settings.action_plan_templates.publish.yes", "Yes, publish")}
                </Button>
                <Button fullWidth variant="secondary" disabled={mutation.isLoading} onClick={close}>
                    {t("settings.action_plan_templates.publish.cancel", "No, cancel")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
